/**
 * Module: screens/creator/useCreatorOptions
 * Role: Shared hook for the creator wizard option lists (BUG-1144).
 *       Encapsulates the `getCreatorOptions` query AND the auth-readiness gate
 *       so all 4 profile steps (categories/formats/demographics/cities) stay DRY
 *       and the query never fires with `Bearer null` on a cold deep-link.
 *
 *       Why the gate is reactive (not a bare isAuthReady() snapshot):
 *       React Query's `enabled` re-evaluates on re-render; a module-level snapshot
 *       read at mount does NOT re-render when the cold-deeplink bootstrap finishes
 *       afterwards. useAuthReady() holds the flag in React state via
 *       subscribeAuthReady, so `enabled` flips false→true and the query auto-runs.
 *
 * Uses:
 *   - react:useState, useEffect
 *   - @tanstack/react-query:useQuery
 *   - api/client:isAuthReady, subscribeAuthReady
 *   - api/creator:getCreatorOptions, CreatorOptionsResponse
 *
 * Used by:
 *   - screens/creator/steps/CategoriesStep.tsx
 *   - screens/creator/steps/FormatsStep.tsx
 *   - screens/creator/steps/DemographicsStep.tsx
 *   - screens/creator/steps/CitiesStep.tsx
 *
 * ADR-023: auth-readiness is NOT a platform global — this hook lives outside
 * src/platform/ and touches no window.* platform objects.
 * Glossary: ai/glossary/buyer.md
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAuthReady, subscribeAuthReady } from '../../api/client';
import { getCreatorOptions, type CreatorOptionsResponse } from '../../api/creator';

/**
 * Reactive auth-readiness flag. Seeds from the synchronous snapshot, then
 * subscribes so a late bootstrap (cold deep-link) flips it true and re-renders.
 */
export function useAuthReady(): boolean {
  const [ready, setReady] = useState<boolean>(isAuthReady());

  useEffect(() => {
    // Re-sync on mount in case the token landed between initial useState and effect.
    setReady(isAuthReady());
    const unsubscribe = subscribeAuthReady(() => setReady(isAuthReady()));
    return unsubscribe;
  }, []);

  return ready;
}

export interface UseCreatorOptionsResult {
  options: CreatorOptionsResponse | undefined;
  isLoading: boolean;
}

/**
 * Loads the creator wizard option lists, gated on auth-readiness.
 * `isLoading` is true while the query is genuinely fetching OR while we are
 * waiting for auth (so steps keep showing «Загрузка…» instead of empty chips).
 */
export function useCreatorOptions(): UseCreatorOptionsResult {
  const authReady = useAuthReady();

  const { data: options, isLoading } = useQuery({
    queryKey: ['creator', 'options'],
    queryFn: getCreatorOptions,
    staleTime: Infinity,
    enabled: authReady,
  });

  return {
    options,
    // Gated-off queries report isLoading=false; surface the wait as loading so
    // the step shows the spinner, not an empty options list, until auth is ready.
    isLoading: isLoading || !authReady,
  };
}
